import React, { useEffect, useMemo, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Save } from "lucide-react";
import { toast } from "sonner";

const finalFor = (std) => Math.ceil((Number(std) || 0) * 1.18 / 5000) * 5000;
const titleCase = (s) => (s || "").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

export default function PricesPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/containers").then(r => setRows(r.data.map(c => ({ ...c, _orig_std: c.standard_price }))));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter(r => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return `${r.name} ${r.category} ${r.class_code}`.toLowerCase().includes(t);
  }), [rows, q]);

  const setStd = (id, val) => setRows(rows.map(r => r.id === id ? { ...r, standard_price: val } : r));

  const save = async (r) => {
    setSaving(r.id);
    try {
      await api.patch(`/containers/${r.id}/price`, { standard_price: Number(r.standard_price) || 0 });
      toast.success(`Saved — final ${fmtINR(finalFor(r.standard_price))}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(null); }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-900">Edit Standard Price</h1>
      <p className="text-sm text-slate-500 mt-1">Change the basic price — GST at 18% and the final rounded price update automatically.</p>

      <div className="relative mt-4">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search container…"
          className="pl-10 h-11 rounded-xl bg-white" data-testid="prices-search-input" />
      </div>

      <div className="space-y-3 mt-4" data-testid="prices-list">
        {filtered.map(r => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`price-row-${r.id}`}>
            <div className="text-sm font-semibold text-slate-900">{r.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{titleCase(r.category)}{r.class_code ? ` · ${r.class_code}` : ""} · now {fmtINR(r.final_price)} final</div>
            <div className="flex gap-2 mt-3">
              <Input type="number" value={r.standard_price ?? ""} onChange={e => setStd(r.id, e.target.value)}
                className="h-11 rounded-xl font-mono-tab flex-1" data-testid={`price-std-${r.id}`} />
              <button onClick={() => save(r)} disabled={saving === r.id} data-testid={`price-save-${r.id}`}
                className="h-11 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-1.5 shrink-0 transition-colors disabled:opacity-60">
                <Save className="w-4 h-4" /> {saving === r.id ? "Saving…" : "Save"}
              </button>
            </div>
            {Number(r.standard_price) !== Number(r._orig_std) && (
              <div className="text-xs text-blue-600 mt-2" data-testid={`price-preview-${r.id}`}>→ {fmtINR(finalFor(r.standard_price))} final (incl. 18% GST)</div>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div className="text-center py-10 text-slate-500 text-sm">No containers match.</div>}
      </div>
    </div>
  );
}
